import produce from "immer"
import moment from "moment"
import type { PagingActions, PagingOptions } from "../../models/types"
import { loadPagingOptions } from "../../utils/sort-table.utils"
import type { Attachment } from "../projects/projects.types"
import type { TopographyMetadataDto } from "./../../models/models"
import type { AppAction } from "./../app.types"
import {
  METADATA_ADD_ATTACHMENT,
  METADATA_LOAD,
  METADATA_LOAD_BY_PAGE,
  METADATA_LOAD_CATEGORIES,
  METADATA_LOAD_DEFAULT,
  METADATA_LOAD_LIST,
  METADATA_LOAD_LIST_OPTIONS,
  METADATA_UPDATE_ATTACHMENT,
  METADATA_UPDATE_ATTACHMENTS
} from "./metadata.actions"
import type { IMetadataState, Metadata, MetadataActions } from "./metadata.types"

const metadataInitialState: IMetadataState = {
  metadataList: [],
  metadata: getEmptyMetadata(),
  categories: [],
  pagingOptions: {
    currentPage: 0,
    totalPages: 0,
    pageSize: 10
  },
  topographyMetadata: {
    id: ""
  }
}

export function metadataReducer(
  currentState: IMetadataState | undefined,
  action: MetadataActions | PagingActions | AppAction<string[]>
): IMetadataState {
  const state = currentState ?? metadataInitialState
  switch (action.type) {
    case METADATA_LOAD_DEFAULT: {
      return loadMetadata(state, getEmptyMetadata())
    }
    case METADATA_LOAD_LIST: {
      return loadMetadataList(state, action.payload as Metadata[])
    }
    case METADATA_LOAD: {
      return loadMetadata(state, action.payload as Metadata)
    }
    case METADATA_LOAD_LIST_OPTIONS: {
      return loadPagingOptions(state, action.payload as PagingOptions)
    }
    case METADATA_ADD_ATTACHMENT: {
      return addAttachment(state, action.payload as Attachment)
    }
    case METADATA_UPDATE_ATTACHMENTS: {
      return updateAttachments(state, action.payload as Attachment[])
    }
    case METADATA_UPDATE_ATTACHMENT: {
      return updateAttachment(state, action.payload as Attachment)
    }
    case METADATA_LOAD_CATEGORIES: {
      return loadCategories(state, action.payload as string[])
    }
    case METADATA_LOAD_BY_PAGE: {
      return loadByPage(state, action.payload as TopographyMetadataDto)
    }
    default: {
      return state
    }
  }
}

function loadMetadataList(currentState: IMetadataState, metadatas: Metadata[]): IMetadataState {
  return produce(currentState, (draftState: IMetadataState) => {
    draftState.metadataList = metadatas
  })
}

function loadMetadata(currentState: IMetadataState, metadata: Metadata): IMetadataState {
  return produce(currentState, (draftState: IMetadataState) => {
    draftState.metadata = metadata
  })
}

function addAttachment(currentState: IMetadataState, attachment: Attachment): IMetadataState {
  return produce(currentState, (draftState: IMetadataState) => {
    if (!draftState.metadata) return

    draftState.metadata.attachments.push(attachment)
  })
}

function updateAttachments(currentState: IMetadataState, attachments: Attachment[]): IMetadataState {
  return produce(currentState, (draftState: IMetadataState) => {
    if (!draftState.metadata) return

    draftState.metadata.attachments = attachments
  })
}

function updateAttachment(currentState: IMetadataState, attachment: Attachment): IMetadataState {
  return produce(currentState, (draftState: IMetadataState) => {
    if (!draftState.metadata) return

    draftState.metadata.attachments[attachment.order - 1] = attachment
  })
}

function loadCategories(currentState: IMetadataState, categories: string[]): IMetadataState {
  return produce(currentState, (draftState: IMetadataState) => {
    draftState.categories = categories
  })
}

function loadByPage(currentState: IMetadataState, topographyMetadata: TopographyMetadataDto): IMetadataState {
  return produce(currentState, (draftState: IMetadataState) => {
    draftState.topographyMetadata = topographyMetadata
  })
}

function getEmptyMetadata(): Metadata {
  return {
    title: "",
    aliasses: "",
    assetId: "",
    category: "",
    dateOfMap: moment().format("yyyy-MM-DD"),
    isDigitalOnly: false,
    pageNumber: 0,
    pageSection: "",
    chapterId: " ",
    topicChapterId: " ",
    regionId: " ",
    themeId: " ",
    licenceIds: [],
    attachments: [],
    showUnderChapter: false,
    showUnderRegion: false
  }
}
